/**
 * Adds a position, transaction, and updates account balance for a given user order.
 * @param {Object} userOrder - The user order object
 * @param {Function} dispatch - Redux dispatch function
 */
export const fulfillUserOrder = (userOrder, dispatch) => {
  if (!userOrder || !dispatch) return;
  // Add position
  dispatch(addPosition({
    entityId: userOrder.entityId,
    entityName: userOrder.entityName,
    price: userOrder.price,
    quantity: userOrder.quantity,
    type: userOrder.type,
    timestamp: new Date().toISOString(),
    orderId: userOrder.id
  }));
  // Add transaction
  dispatch(addTransaction({
    orderId: userOrder.id,
    entityId: userOrder.entityId,
    entityName: userOrder.entityName,
    type: userOrder.type,
    quantity: userOrder.quantity,
    price: userOrder.price,
    status: 'fulfilled',
    timestamp: new Date().toISOString()
  }));
  // Deduct from account balance if buy order
  if (userOrder.type === 'buy') {
    const value = userOrder.price * userOrder.quantity;
    dispatch(decrementBalance(value));
  }
};
// Market Maker Service - Handles opposite side order creation
import { addMarketMakerOrder } from '../store/marketMakerOrdersSlice';
import { decrementBalance } from '../store/accountSlice';
import { addPosition } from '../store/positionsSlice';
import { addTransaction } from '../store/transactionsSlice';

/**
 * Creates a market maker order that takes the opposite side of a user order
 * @param {Object} userOrder - The user's order
 * @param {Object} entity - The entity being traded
 * @returns {Object} Market maker order
 */
export const createOppositeSideOrder = (userOrder, entity) => {
  if (!userOrder || !entity) {
    throw new Error('User order and entity are required');
  }

  // Determine the opposite side
  const oppositeType = userOrder.type === 'buy' ? 'sell' : 'buy';
  
  // Use the appropriate price (ask for market maker sell, bid for market maker buy)
  const price = oppositeType === 'sell' ? entity.ask : entity.bid;

  const marketMakerOrder = {
    id: `mm_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    userOrderId: userOrder.id, // Reference to the original user order
    entityId: userOrder.entityId,
    entityName: userOrder.entityName,
    type: oppositeType,
    quantity: userOrder.quantity,
    price: price,
    timestamp: new Date().toISOString(),
    status: 'pending',
    owner: 'market-maker',
    purpose: 'liquidity', // or 'offset' for position management
    spread: entity.spread,
    matchedWith: userOrder.id
  };

  return marketMakerOrder;
};

/**
 * Creates an offsetting order to manage market maker position
 * @param {Array} marketMakerOrders - Current market maker orders
 * @param {Object} entity - The entity to create offset for
 * @returns {Object|null} Offset order or null if no offset needed
 */
export const createOffsettingOrder = (marketMakerOrders, entity) => {
  // Calculate net position
  const entityOrders = marketMakerOrders.filter(order => order.entityId === entity.id);
  const buyOrders = entityOrders.filter(order => order.type === 'buy');
  const sellOrders = entityOrders.filter(order => order.type === 'sell');
  
  const buyQuantity = buyOrders.reduce((sum, order) => sum + order.quantity, 0);
  const sellQuantity = sellOrders.reduce((sum, order) => sum + order.quantity, 0);
  const netPosition = buyQuantity - sellQuantity;

  // If net position is 0, no offset needed
  if (netPosition === 0) {
    return null;
  }

  // Create offset order
  const offsetType = netPosition > 0 ? 'sell' : 'buy';
  const offsetQuantity = Math.abs(netPosition);
  const price = offsetType === 'sell' ? entity.ask : entity.bid;

  const offsetOrder = {
    id: `mm_offset_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    entityId: entity.id,
    entityName: entity.name,
    type: offsetType,
    quantity: offsetQuantity,
    price: price,
    timestamp: new Date().toISOString(),
    status: 'pending',
    owner: 'market-maker',
    purpose: 'offset',
    spread: entity.spread,
    netPosition: netPosition
  };

  return offsetOrder;
};

/**
 * Calculates market maker profit from executed orders
 * @param {Array} marketMakerOrders - Market maker orders
 * @returns {Object} Profit summary
 */
export const calculateMarketMakerProfit = (marketMakerOrders) => {
  const executedOrders = marketMakerOrders.filter(order => order.status === 'completed');
  
  let totalProfit = 0;
  let totalBuyValue = 0;
  let totalSellValue = 0;
  
  executedOrders.forEach(order => {
    const orderValue = order.quantity * order.price;
    
    if (order.type === 'buy') {
      totalBuyValue += orderValue;
    } else {
      totalSellValue += orderValue;
    }
  });
  
  totalProfit = totalSellValue - totalBuyValue;
  
  return {
    totalProfit,
    totalBuyValue,
    totalSellValue,
    executedOrders: executedOrders.length,
    profitPerOrder: executedOrders.length > 0 ? totalProfit / executedOrders.length : 0
  };
};

/**
 * Matches user orders with market maker orders
 * @param {Array} userOrders - User orders
 * @param {Array} marketMakerOrders - Market maker orders
 * @returns {Array} Array of matched orders
 */
export const matchOrders = (userOrders, marketMakerOrders) => {
  const matches = [];
  const pendingUserOrders = userOrders.filter(order => order.status === 'pending');
  const pendingMarketMakerOrders = marketMakerOrders.filter(order => order.status === 'pending');

  pendingUserOrders.forEach(userOrder => {
    const matchingMarketMakerOrder = pendingMarketMakerOrders.find(mmOrder => 
      mmOrder.entityId === userOrder.entityId &&
      mmOrder.quantity === userOrder.quantity &&
      mmOrder.type !== userOrder.type &&
      mmOrder.userOrderId === userOrder.id
    );

    if (matchingMarketMakerOrder) {
      matches.push({
        userOrder,
        marketMakerOrder: matchingMarketMakerOrder,
        executionPrice: matchingMarketMakerOrder.price,
        timestamp: new Date().toISOString()
      });
    }
  });

  return matches;
};

/**
 * Processes a user order and creates the corresponding market maker order
 * @param {Object} userOrder - The user's order
 * @param {Object} entity - The entity being traded
 * @param {Function} dispatch - Redux dispatch function
 * @param {Array} marketMakerOrders - Current market maker orders
 */
export const processUserOrder = (userOrder, entity, dispatch, marketMakerOrders) => {
  try {

    // Try to match with an existing market maker order (opposite type, same entity, not fulfilled/cancelled)

    const oppositeType = userOrder.type === 'buy' ? 'sell' : 'buy';
    const match = marketMakerOrders.find(mmo =>
      mmo.type === oppositeType &&
      mmo.entityId === userOrder.entityId &&
      mmo.status !== 'fulfilled' &&
      mmo.status !== 'cancelled' &&
      mmo.quantity === userOrder.quantity
    );
    if (match) {
      // Mark both as fulfilled
      dispatch({ type: 'orders/updateOrder', payload: { id: userOrder.id, updates: { status: 'fulfilled' } } });
      dispatch({ type: 'marketMakerOrders/updateMarketMakerOrder', payload: { id: match.id, updates: { status: 'fulfilled' } } });

      // Create a new position object for the fulfilled user order
      dispatch(addPosition({
        entityId: userOrder.entityId,
        entityName: userOrder.entityName,
        price: userOrder.price,
        quantity: userOrder.quantity,
        type: userOrder.type,
        timestamp: new Date().toISOString(),
        orderId: userOrder.id
      }));
      // Add transaction for fulfilled user order
      dispatch(addTransaction({
        orderId: userOrder.id,
        entityId: userOrder.entityId,
        entityName: userOrder.entityName,
        type: userOrder.type,
        quantity: userOrder.quantity,
        price: userOrder.price,
        status: 'fulfilled',
        timestamp: new Date().toISOString()
      }));

      // Deduct from account balance if buy order is fulfilled
      if (userOrder.type === 'buy') {
        const value = userOrder.price * userOrder.quantity;
        dispatch(decrementBalance(value));
      }

      return { success: true, matchedOrder: match };
    }

    // No match: create opposite side order
    const oppositeOrder = createOppositeSideOrder(userOrder, entity);
    dispatch(addMarketMakerOrder(oppositeOrder));

    // Check if offset is needed
    const offsetOrder = createOffsettingOrder([...marketMakerOrders, oppositeOrder], entity);
    if (offsetOrder) {
      dispatch(addMarketMakerOrder(offsetOrder));
    }

    return { success: true, oppositeOrder, offsetOrder };
  } catch (error) {
    console.error('Error processing user order:', error);
    return { success: false, error: error.message };
  }
};